/**
 * Authentication state for the whole client.
 *
 * On mount it asks the API who is signed in. Until that answers, the app shows
 * a loading state rather than the login page - otherwise a returning user with
 * a perfectly valid cookie sees a login form flash before being let in.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ApiError, authApi } from './api.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // 'checking' | 'ready' - 'ready' means the answer is known, not that
  // somebody is signed in.
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    let cancelled = false;

    authApi
      .me()
      .then((data) => {
        if (!cancelled) setUser(data.user);
      })
      .catch((error) => {
        // 401 is the expected answer for a signed-out visitor, not an error
        // worth surfacing. Anything else is logged for the developer.
        if (!(error instanceof ApiError) || !error.isUnauthenticated) {
          console.error('Session check failed:', error);
        }
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setStatus('ready');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await authApi.login(email, password);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      // Clear locally regardless: if the call failed the cookie may still be
      // gone, and leaving a stale user on screen is worse than an extra login.
      setUser(null);
    }
  }, []);

  /**
   * Called by any view that gets a 401 mid-session (an expired cookie). Drops
   * the user so the router falls back to the login page.
   */
  const handleSessionExpired = useCallback(() => setUser(null), []);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      isChecking: status === 'checking',
      login,
      logout,
      handleSessionExpired,
    }),
    [user, status, login, logout, handleSessionExpired],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an <AuthProvider>');
  return context;
}
